import {useFetch} from '#app'
import {showToast} from 'vant'
import {useRoute} from 'vue-router'
import {useI18n} from 'vue-i18n'
import {ref} from 'vue'
import {markOrderCreated} from '~/utils/payFlags'

export function usePay() {
    const route = useRoute()
    const {t} = useI18n()
    const loading = ref(false) // ✅ 全局 loading

    // ========== Service 层 ==========
    const createBusinessOrder = async (product: any, extra: any = {}) => {
        const body = {
            rsp_id: product.rsp_id || '',
            book_id: extra.book_id || "",
            chapter_id: extra.chapter_id || "",
            pay_entry: extra.pay_entry || "",
        }
        console.log('看看当前商品:', product)
        console.log('看看创建订单的参数', body)
        //debugger
        const res = await useFetch('/api/pay/create-order', {
            method: 'POST',
            body,
            key: (Date.now()).toString()
        })
        // V2.3.0 挽留门槛：创建订单成功打会话标记，本次阅读会话内不再弹挽留
        if (res.data.value?.code === 0) markOrderCreated()
        return res.data.value
    }

    // 构造支付回调地址（return_url / cancel_url / mit_management_url），前置组件与跳转模式共用
    const buildCallbackUrls = (extra: any = {}) => {
        // 判断是否是阅读页或者落地页,落地页和阅读页需要保留原生参数，目前只有id
        const isReader = window.location.pathname.includes('/reader')
        const isAds = window.location.pathname.includes('/ads')
        let return_url = ''
        let cancel_url = ''
        let mit_management_url = '' // payermax专用
        const originParams = ['id']

        // 获取当前 URL 的查询参数
        const url = new URL(window.location.href)
        const searchParams = new URLSearchParams()

        originParams.forEach((key) => {
            if (url.searchParams.has(key)) {
                searchParams.set(key, url.searchParams.get(key) as string)
            }
        })

        // 添加 guest_id 到回调 URL，用于 Cookie 丢失时恢复
        const guestIdCookie = useCookie('guest_id').value
        if (guestIdCookie) {
          searchParams.set('guest_id', guestIdCookie)
        }

        if (isReader) {
            // 阅读页/落地页：只需要 return_url，并带上原生参数,都跳转到阅读页弹出支付结果弹窗
            return_url = window.location.origin + window.location.pathname
            cancel_url = window.location.origin + window.location.pathname
            mit_management_url = window.location.origin + window.location.pathname
            if ([...searchParams].length > 0 || window.location.pathname.includes('/ads')) {
                return_url += `?${searchParams.toString()}&chapter_id=${extra.chapter_id}`
                cancel_url += `?${searchParams.toString()}&chapter_id=${extra.chapter_id}`
                mit_management_url += `?${searchParams.toString()}&chapter_id=${extra.chapter_id}`
            }
        } else if (isAds) {
            return_url = window.location.origin + '/reader'
            cancel_url = window.location.origin + '/reader'
            mit_management_url = window.location.origin + '/reader'
            return_url += `?id=${extra.book_id}&chapter_id=${extra.chapter_id}`
            cancel_url += `?id=${extra.book_id}&chapter_id=${extra.chapter_id}`
            mit_management_url += `?id=${extra.book_id}&chapter_id=${extra.chapter_id}`
        } else {
            const base = window.location.origin + window.location.pathname
            return_url = base
            cancel_url = base
            mit_management_url = base
        }
        return { return_url, cancel_url, mit_management_url }
    }

    const createPayment = async (order_no: string, paymentType: string, extra: any = {}, payMethod: string = 'paypal') => {
        const { return_url, cancel_url, mit_management_url } = buildCallbackUrls(extra)
        console.log('看看落地页有没有带上章节ID', return_url, cancel_url)
        const body = {
            order_no,
            payment_type: paymentType,
            return_url,
        }
        if (payMethod === 'paypal') {
            body.cancel_url = cancel_url
        } else {
            body.mit_management_url = mit_management_url
            body.pay_channel = payMethod
        }
        const path = payMethod === 'paypal' ? '/api/pay/paypal-create-payment' : '/api/pay/payermax-create-payment'
        const res = await useFetch(path, {
            method: 'POST',
            body,
            key: (Date.now()).toString(),
        })
        return res.data.value
    }

    const captureOrder = async () => {
        const type = route.query.payment_type
        const payChannel = route.query.pay_channel
        // 非paypal跳过捕获
        if (type && type === 'order' && payChannel === 'paypal') {
            const payOrderId = route.query.pay_order_id
            const body = {
                order_no: route.query.order_no || '',
                pay_order_id: payOrderId && payOrderId !== '' ? payOrderId : route.query.token || ''
            }
            try {
                loading.value = true
                const res = await useFetch('/api/pay/capture-order', {
                    method: 'POST',
                    body
                })
                return res.data.value
            } catch (e) {
                console.error(e)
                showToast(t('other.pay_fail'))
                return null
            } finally {
                loading.value = false
            }
        }
    }

    // 确认订单状态以弹出对应的弹窗
    const confirmOrderStatus = async () => {
        const payChannel = route.query.pay_channel
        const order_no = route.query.order_no
        const orderToken = payChannel === 'paypal' ? route.query.token : route.query.tradeToken
        const type = route.query.payment_type
        const commit = route.query.commit // paypal用这个判断支付状态
        const status = route.query.status // payermax用这个判断支付状态
        const subscriptionId = route.query.subscription_id
        // 前置组件支付在 SDK 内当场完成，前端无 tradeToken，仅凭 order_no + commit 确认（后端会用 order_no 再核实）
        const isDropIn = ['card-payermax', 'googlepay-payermax', 'applepay-payermax'].includes(payChannel as string)
        let isValid = false
        if (isDropIn) {
            isValid = Boolean(order_no && commit === 'confirm')
        } else if (type === 'order') {
            isValid = Boolean((order_no && orderToken && commit === 'confirm'))
        } else if (type === 'subscription') {
            isValid = Boolean((payChannel === 'paypal' && order_no && orderToken && subscriptionId && commit === 'confirm') || order_no && orderToken && commit === 'confirm')
        }
        if (isValid) {
            const body = {
                order_no,
            }
            const res = await $fetch('/api/pay/confirm-order', {
                method: 'POST',
                body
            })
            console.log('hooks订单状态-------->>>', res)
            return res
        } else {
            showToast(t('reader.recharge_failed'))
        }
    }

    // ========== PayerMax 前置组件 ==========
    // 创建前置组件会话信息（返回 client_key/session_key/sandbox；不再绑定商品，下单/订阅计划另取）
    const createDropInSession = async (rsp_id: string, extra: any = {}) => {
        // 需带下单参数；book_id/chapter_id 仅阅读器充值时可获得，有值才传
        const body: any = { rsp_id, pay_entry: extra.pay_entry || '' }
        if (extra.book_id) body.book_id = extra.book_id
        if (extra.chapter_id) body.chapter_id = extra.chapter_id
        const res = await $fetch('/api/pay/payermax-create-drop-in-session', {
            method: 'POST',
            body
        })
        return res
    }

    // 前置组件下单（与收银台同接口 /order/create，用 $fetch 以兼容 Apple Pay 手势栈内「发起 Promise 不 await」调用）
    // 参数与 createDropInSession 建 session 同源（rsp_id 来自 product，book_id/chapter_id/pay_entry 来自 extra）
    const createDropInOrder = async (product: any, extra: any = {}, payMethod: string = '') => {
        const body: any = {
            rsp_id: product?.rsp_id || '',
            book_id: extra.book_id || '',
            chapter_id: extra.chapter_id || '',
            pay_entry: extra.pay_entry || ''
        }
        if (payMethod) {
            body.pay_channel = payMethod
        }
        const res = await $fetch('/api/pay/create-order', {
            method: 'POST',
            body
        })
        // V2.3.0 挽留门槛：同 createBusinessOrder，前置组件下单成功也打标记
        if (res?.code === 0) markOrderCreated()
        return res
    }

    // 创建前置组件支付请求（SDK 获取 payment_token 后调用）
    const createDropInPayment = async (params: any) => {
        const res = await $fetch('/api/pay/payermax-create-drop-in-payment', {
            method: 'POST',
            body: params
        })
        return res
    }

    // ========== 辅助封装 ==========
    const safeRequest = async <T>(
        fn: () => Promise<T>,
        failMsg: string
    ): Promise<T | null> => {
        try {
            loading.value = true
            return await fn()
        } catch (e) {
            console.error(e)
            showToast(failMsg)
            return null
        } finally {
            loading.value = false
        }
    }

    // ========== 整体支付链路 ==========
    const startPay = async (product: any, paymentType: string, extra: any = {}, payMethod: string = 'paypal') => {
        if (loading.value) return

        // 1. 创建业务订单
        const orderRes = await safeRequest(
            () => createBusinessOrder(product, extra),
            t('other.create_order_fail')
        )
        if (!orderRes || orderRes.code !== 0) return showToast(t('other.create_order_fail'))

        // 2. 创建支付请求
        const payRes = await safeRequest(
            () => createPayment(orderRes.data.order_no, paymentType, extra, payMethod),
            t('other.create_order_fail')
        )
        if (!payRes || payRes.code !== 0) return

        // 3. 跳转到第三方支付页面
        window.location.href = payRes.data.approve_url
        return {
            status: 'read_to_go',
            code: 200
        }
    }

    return {
        loading,        // ✅ 暴露 loading
        startPay,       // 业务下单 + 支付请求
        captureOrder,   // 支付完成后捕获订单
        confirmOrderStatus, // 支付完成后确认订单状态
        createBusinessOrder,  // 创建业务订单（前置组件需单独下单）
        createDropInSession,  // PayerMax 前置组件：创建会话
        createDropInOrder,    // PayerMax 前置组件：下单（/order/create，$fetch）
        createDropInPayment,  // PayerMax 前置组件：创建支付
        buildCallbackUrls     // 构造回调地址（前置组件成功跳转用）
    }
}